import { ReadonlyURLSearchParams } from 'next/navigation'
import {
  PATIENT_REDIRECT_ALLOWLIST,
  PATIENT_REDIRECT_DENYLIST,
  CLIENT_REDIRECT_DENYLIST,
} from '@/utilities/paths'

type SearchParamsInput = URLSearchParams | ReadonlyURLSearchParams | null | undefined
type RedirectPortal = 'patient' | 'client'

const getBase = (): string | undefined =>
  typeof window === 'undefined' ? process.env.NEXT_PUBLIC_SERVER_URL : window.location.origin

// Two-step parse to catch both plain `../` and percent-encoded traversal variants like
// `%2e%2e%2f` (which the URL spec does NOT resolve as a path separator in a single parse).
const getNormalizedUrl = (path: string, base: string): URL | null => {
  try {
    const first = new URL(path, base)
    const decodedPathname = decodeURIComponent(first.pathname)
    return new URL(decodedPathname + first.search + first.hash, base)
  } catch {
    return null
  }
}

export const isValidPatientRedirectPath = (path: string): boolean => {
  const base = getBase()
  if (!base) return false
  const url = getNormalizedUrl(path, base)
  if (url?.origin !== new URL(base).origin) return false
  if (PATIENT_REDIRECT_DENYLIST.some((d) => url.pathname.startsWith(d))) return false
  return PATIENT_REDIRECT_ALLOWLIST.some((p) => url.pathname.startsWith(p))
}

export const isValidClientRedirectPath = (path: string): boolean => {
  const base = getBase()
  if (!base) return false
  const url = getNormalizedUrl(path, base)
  if (url?.origin !== new URL(base).origin) return false
  if (
    CLIENT_REDIRECT_DENYLIST.some((d) => url.pathname === d || url.pathname.startsWith(`${d}/`))
  ) {
    return false
  }
  return url.pathname.startsWith('/client/')
}

// Cookie name for the redirect intent. Used by middleware (write) and post-auth handler (read).
const COOKIE_NAME = 'redirect_intent'
// 15 minutes — long enough to complete any auth flow, short enough to avoid stale redirects.
const COOKIE_MAX_AGE = 900

// Returns the validated, canonical redirectTo from the URL, or null.
export const getValidatedRedirectTo = (
  searchParams: SearchParamsInput,
  portal: RedirectPortal,
): string | null => {
  const raw = searchParams?.get('redirectTo')
  if (!raw) return null
  const base = getBase()
  if (!base) return null
  const isValid =
    portal === 'client' ? isValidClientRedirectPath(raw) : isValidPatientRedirectPath(raw)
  if (!isValid) return null
  const url = getNormalizedUrl(raw, base)
  if (!url) return null
  return `${url.pathname}${url.search}${url.hash}`
}

// Writes a validated redirect intent to a domain-scoped cookie.
// Called on mount of the login page for session-expiry redirects (where middleware does not
// intercept the request because /login is a public path).
// For cold deep links, the cookie is set by middleware — this function is not needed.
// Writing the cookie twice under React Strict Mode is harmless: same value, same expiry.
export const persistRedirectIntent = (
  searchParams: SearchParamsInput,
  portal: RedirectPortal,
): void => {
  if (typeof document === 'undefined') return
  const validated = getValidatedRedirectTo(searchParams, portal)
  if (validated) {
    document.cookie = `${COOKIE_NAME}=${encodeURIComponent(validated)}; path=/; max-age=${COOKIE_MAX_AGE}; secure; samesite=lax`
  }
}

// Reads and immediately clears the redirect_intent cookie.
// URL param takes precedence; cookie is the fallback for out-of-band flows (magic link, OAuth).
// Always clears the cookie regardless of source to prevent stale reuse.
//
// IMPORTANT: Do NOT call this inside a bare useEffect — keep it inside the post-auth async
// handler only. A useEffect would run on every render, potentially clearing the cookie before
// the auth handler reads it.
export const readAndClearRedirectIntent = (
  searchParams: SearchParamsInput,
  portal: RedirectPortal,
): string | null => {
  const fromUrl = getValidatedRedirectTo(searchParams, portal)

  // Read the cookie value (domain-scoped, survives cross-tab magic link / OAuth flows).
  let fromCookie: string | null = null
  if (typeof document !== 'undefined') {
    const cookieValueRegex = new RegExp(`(?:^|; )${COOKIE_NAME}=([^;]*)`)
    const match = cookieValueRegex.exec(document.cookie)
    if (match) {
      // decodeURIComponent throws a synchronous URIError on malformed sequences (e.g. a
      // malicious browser extension writing a broken value). Without a try/catch, this would
      // crash the entire post-auth handler and lock the user out of the app.
      let decoded: string | null = null
      try {
        decoded = decodeURIComponent(match[1] || '')
      } catch {
        // Silent fallback: treat a malformed cookie as if it were absent.
      }
      if (decoded) {
        const validator =
          portal === 'client' ? isValidClientRedirectPath : isValidPatientRedirectPath
        // Re-validate on read: defense in depth, even though the cookie was written by our own code.
        fromCookie = validator(decoded) ? decoded : null
      }
    }
    // Clear the cookie regardless of validity to prevent stale values.
    document.cookie = `${COOKIE_NAME}=; path=/; max-age=0; secure; samesite=lax`
  }

  return fromUrl ?? fromCookie ?? null
}

// Replaces dynamic path segments that may contain PHI before passing to analytics.
// Extend this function if new sensitive routes are added.
export const redactSensitivePath = (path: string): string =>
  path.replace(/\/kit-results\/[^/?#]+/, '/kit-results/[id]')

// Appends a validated ?redirectTo= param to a login URL built by the middleware.
// Mutates the URL in place; no-op when the intended path is empty or invalid.
export const appendRedirectTo = (
  loginUrl: URL,
  reqUrl: string,
  pathname: string,
  search: string,
): void => {
  const intendedPath = sanitizeRedirectPath(reqUrl, `${pathname}${search}`, '')
  if (intendedPath) loginUrl.searchParams.set('redirectTo', intendedPath)
}

// Ensure redirectCandidateUrl does not point at an external domain.
export function sanitizeRedirectPath(
  urlWithRequiredOrigin: string,
  redirectCandidateUrl: string,
  fallback = '/',
): string {
  try {
    const resolved = new URL(redirectCandidateUrl ?? fallback, urlWithRequiredOrigin)
    if (resolved.origin !== new URL(urlWithRequiredOrigin).origin) return fallback
    return resolved.pathname + resolved.search + resolved.hash
  } catch {
    return fallback
  }
}
